import * as React from 'react';
import { BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FileRejection, useDropzone } from 'react-dropzone';
import { Paperclip } from '@liveauctioneers/hammer-ui-core/icons';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { takeRight } from 'es-toolkit';
import { useQuill } from 'react-quilljs';
import ActionText from '@liveauctioneers/caterwaul-components/lib/ActionText/ActionText';
import AttachedFileList from './components/AttachedFileList';
import AttachFilesPlaceholder from './components/AttachFilesPlaceholder';
import QuillTheme from './components/QuillTheme';
import styled, { css } from 'styled-components';

type Props = {
    readonly onAddAttachment: Function;
    readonly onDeleteAttachment: Function;
    readonly onSendMessage: Function;
    readonly testid?: string;
};

const RichMessageEditor = ({
    onAddAttachment,
    onDeleteAttachment,
    onSendMessage,
    testid = 'messengerMessageEditor',
}: Props) => {
    const [attachedFiles, setAttachedFiles] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [editorIsEmpty, setEditorIsEmpty] = React.useState(true);

    // Add files to state when attached
    const onDrop = React.useCallback(
        (acceptedFiles: any[]) => {
            setLoading(true);
            const attachedFileNames = attachedFiles.map((attachedFile) => attachedFile.name);
            const filteredAcceptedFiles = acceptedFiles.filter((acceptedFile) => {
                return !attachedFileNames.includes(acceptedFile.name);
            });
            const filteredAcceptedFilesSize = filteredAcceptedFiles.reduce((a, b) => a + b.size, 0);

            if (filteredAcceptedFilesSize > 10000000) {
                setHasError(true);
                setErrorMessage('Your uploaded file size has exceeded 10MB.');
                setLoading(false);
                return () => [];
            } else if (filteredAcceptedFiles.length < acceptedFiles.length) {
                setHasError(true);
                setErrorMessage('You cannot upload multiple files with the same file name.');
                setLoading(false);
                return () => [];
            } else if (attachedFiles.length + filteredAcceptedFiles.length > 5) {
                setHasError(true);
                setErrorMessage('Your total files have exceeded 5.');
                setLoading(false);
                return () => [];
            } else {
                setHasError(false);
                setErrorMessage('');

                let lastFive: any[] = [];
                const fileList = [
                    // prepend prev files to list
                    ...attachedFiles,
                    // append newly attached files, generate url to local file
                    ...filteredAcceptedFiles.map((newFile) =>
                        Object.assign(newFile, {
                            preview: URL.createObjectURL(newFile),
                        })
                    ),
                ];
                // Limit to last five attachments
                lastFive = takeRight(fileList, 5);

                onAddAttachment('', lastFive).then(({ error }: { error: any }) => {
                    setLoading(false);
                    if (!error) {
                        hasError && setHasError(false);
                        setAttachedFiles(lastFive);
                    } else {
                        setHasError(true);
                    }
                });
            }
        },
        [attachedFiles, onAddAttachment, hasError]
    );

    const onDropRejected = React.useCallback((rejectedFiles: FileRejection[]) => {
        setHasError(true);
        setErrorMessage(
            `The following file(s) exceeded the maximum 10MB: ${rejectedFiles
                .map((item) => {
                    return item.file.name;
                })
                .join(', ')}`
        );
    }, []);

    // Create a dropzone for file uploads
    const {
        getInputProps,
        getRootProps,
        isDragActive,
        open: openFileDialog,
    } = useDropzone({
        // Accept all images, pdfs or ms word style documents
        accept: 'image/*,.pdf,.doc,.docx,.csv,.zip,.xls,.xlsx,.numbers,.pages,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        // Maximum size (10MB) per file
        maxSize: 10e6,
        noClick: true,
        onDrop,
        onDropRejected,
    });

    // Quill rich text editor constructor
    const { quill, Quill, quillRef } = useQuill({
        modules: {
            magicUrl: true,
            /**
             * Setup the editor toolbar
             * @see https://quilljs.com/docs/modules/toolbar/
             */
            toolbar: [['bold', 'italic', 'underline', 'link', { list: 'ordered' }, { list: 'bullet' }]],
        },
        placeholder: 'Write a message',
        theme: 'snow',
    });

    // Setup quill plugins
    if (!quill && Quill) {
        const MagicUrl = require('quill-magic-url').default;
        Quill.register('modules/magicUrl', MagicUrl);
    }

    if (quill) {
        // Set flag to disable send button if editor is empty
        quill.on('text-change', () => {
            const isEmpty = quill.getLength() === 1;
            if (isEmpty && !editorIsEmpty) {
                setEditorIsEmpty(true);
            } else if (!isEmpty && editorIsEmpty) {
                setEditorIsEmpty(false);
            }
        });

        quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
            let ops: any[] = [];
            delta.ops.forEach((op) => {
                if (op.insert && typeof op.insert === 'string') {
                    ops.push({
                        insert: op.insert,
                    });
                }
            });
            delta.ops = ops;
            return delta;
        });
    }

    const handleSendMessage = () => {
        setLoading(true);
        if (quill) {
            const { ops: deltaOps } = quill.getContents();
            const config = { inlineStyles: true };
            const deltaToHTML = new QuillDeltaToHtmlConverter(deltaOps, config);
            const messageHTML = deltaToHTML.convert();
            onSendMessage(messageHTML).then(({ error }: { error: any }) => {
                setLoading(false);
                if (!error) {
                    setAttachedFiles([]);
                    hasError && setHasError(false);
                    // @ts-expect-error can't set empty array to a typed array
                    quill.setContents([]);
                } else {
                    setHasError(true);
                }
            });
        }
    };

    const handleDeleteDraft = (fileName: string) => {
        setLoading(true);

        onDeleteAttachment(fileName).then(({ error }: { error: any }) => {
            setLoading(false);
            if (!error) {
                hasError && setHasError(false);
            } else {
                setHasError(true);
            }
        });
    };

    const disabled = loading || attachedFiles.length >= 5;

    return (
        <>
            <FileDropzone
                isDragActive={isDragActive}
                {...getRootProps()}
                data-testid={testid}
                // @ts-ignore disabled not a prop?
                disabled={disabled}
            >
                <input {...getInputProps()} />
                <ContentEditor>
                    {/* The Quill rich text editor*/}
                    <div ref={quillRef} />
                    <BrowseFilesButton
                        disabled={disabled}
                        onClick={openFileDialog}
                    >
                        <AttachFilesText>
                            <Paperclip size="sm" />
                            &nbsp;Browse Files
                        </AttachFilesText>
                    </BrowseFilesButton>
                    {attachedFiles.length ? (
                        <AttachedFileList
                            attachedFiles={attachedFiles}
                            handleDeleteDraft={handleDeleteDraft}
                            loading={loading}
                            setAttachedFiles={setAttachedFiles}
                        />
                    ) : (
                        <AttachFilesPlaceholder />
                    )}
                </ContentEditor>
                <DragOverlay isDragActive={isDragActive}>
                    <H2>Drop files here...</H2>
                </DragOverlay>
            </FileDropzone>
            {hasError && (
                <BodySecondary color="critical">
                    {errorMessage || 'An Error has occurred, please try again later.'}
                </BodySecondary>
            )}
            <div className="my-md flex justify-end space-x-sm">
                <Button
                    aria-label="Send message"
                    className="w-[254px]"
                    disabled={editorIsEmpty && attachedFiles.length === 0}
                    loading={loading}
                    onClick={handleSendMessage}
                >
                    Send
                </Button>
            </div>
        </>
    );
};

export default RichMessageEditor;

const AttachFilesText = styled(BodySecondary)`
    display: flex;
    align-items: center;
    padding-top: 8px;
`;

const BrowseFilesButton = styled(ActionText)`
    margin-left: 20px;
    span {
        color: ${({ theme }) => theme.colors.steel};
    }
`;

const DragOverlay = styled.div<{ isDragActive?: boolean }>`
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border-width: 4px;
    border-radius: 2px;
    border-color: ${({ theme }) => theme.colors.blue100};
    border-style: dashed;
    outline: none;
    ${({ isDragActive }) =>
        isDragActive
            ? css`
                  background-color: ${({ theme }) => theme.colors.grey400}73;
                  z-index: 999;
              `
            : css`
                  background-color: transparent;
                  visibility: hidden;
              `};
`;

const FileDropzone = styled.div`
    position: relative;
    &:focus {
        outline: 0;
    }
`;

const ContentEditor = styled.div`
    border: solid 1px ${({ theme }) => theme.colors.grey400};
    ${QuillTheme}
    && {
        .ql-container,
        .ql-toolbar {
            border: none;
        }

        .ql-toolbar {
            border-bottom: solid 1px ${({ theme }) => theme.colors.grey400};
            padding: 0;

            button {
                width: 44px;
                height: 42px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-right: solid 1px ${({ theme }) => theme.colors.grey400};

                svg {
                    color: ${({ theme }) => theme.colors.steel};
                    width: auto;
                    height: 27px;
                }
            }

            button.ql-active {
                background-color: ${({ theme }) => theme.colors.grey500};

                svg {
                    color: ${({ theme }) => theme.colors.blue100};
                }
            }
        }

        .ql-container {
            font-family: ${({ theme }) => theme.typography.fontPrimary};
            font-size: 16px;
            color: ${({ theme }) => theme.colors.black};
            letter-spacing: 0.5px;
            strong {
                font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
            }
        }

        .ql-editor {
            border: none;
            min-height: 231px;
            padding: 14px 20px;
        }

        /* Placeholder text */
        .ql-editor.ql-blank::before {
            font-family: ${({ theme }) => theme.typography.fontSecondary};
            font-size: 15px;
            left: 20px;
            font-style: normal;
            letter-spacing: 0.5px;
        }

        .ql-editor ul > li {
            list-style-type: disc;
            list-style-position: inside;
        }

        .ql-editor ul li:not(.ql-direction-rtl) {
            padding-left: 0;
        }

        .ql-editor ul li.ql-indent-1:not(.ql-direction-rtl) {
            padding-left: 1.5em;
        }

        .ql-editor ul li.ql-indent-2:not(.ql-direction-rtl) {
            padding-left: 3em;
        }

        .ql-editor ul li.ql-indent-3:not(.ql-direction-rtl) {
            padding-left: 4.5em;
        }

        .ql-editor ul li.ql-indent-4:not(.ql-direction-rtl) {
            padding-left: 6em;
        }

        .ql-editor ul li.ql-indent-5:not(.ql-direction-rtl) {
            padding-left: 7.5em;
        }

        .ql-editor ul li.ql-indent-6:not(.ql-direction-rtl) {
            padding-left: 9em;
        }

        .ql-editor ul li.ql-indent-7:not(.ql-direction-rtl) {
            padding-left: 10.5em;
        }

        .ql-editor ul li.ql-indent-8:not(.ql-direction-rtl) {
            padding-left: 12em;
        }
    }
`;
